import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import courseVideo from "../container/courses/videos.module.css";
import {
  MoreIcons,
  SettingIcon,
  SkipBackButtonIcon,
  SkipForwardButtonIcon,
} from "../svg";
import VimeoPlayer, { extractVimeoId } from "./VimeoPlayer";

export const VideoJS = ({
  options,
  onReady,
  updateVideoDuration,
  urlData,
  showRecordedText = false, // New prop to control when to show the text
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const lastPlaybackTime = useRef(0);
  const [showSettings, setShowSettings] = useState(false);
  const [currentQuality, setCurrentQuality] = useState("1080p");
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const playbackSrc = urlData || options?.sources?.[0]?.src;
  const vimeoId = extractVimeoId(playbackSrc);

  useEffect(() => {
    if (vimeoId) {
      return undefined;
    }
    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);

      const player = videojs(
        videoElement,
        {
          ...options,
          controlBar: {
            ...options.controlBar,
            skipBack: true,
            skipForward: true,
            audioTrackButton: true,
            captionsButton: true,
            chaptersButton: true,
            currentTimeDisplay: true,
            durationDisplay: true,
            fullscreenToggle: true,
            progressControl: true,
            subtitlesButton: true,
            pictureInPictureToggle: true,
            remainingTimeDisplay: true,
            volumePanel: true,
          },
        },
        () => {
          videojs.log("player is ready");
          onReady && onReady(player);

          // Conditionally add "Recorded Video" text under the play button
          if (showRecordedText) {
            const bigPlayButton = player.getChild("bigPlayButton");
            if (bigPlayButton) {
              // Create container for the text
              const textContainer = document.createElement("div");
              textContainer.className = "vjs-recorded-video-text";
              textContainer.innerHTML = `
      <div style="font-weight:bold;">Live Classes Recorded Video</div>
      <div style="font-style:italic; margin-top: -20px; font-weight:normal;">(This recorded video helpful for missed classes)</div>
    `;

              // Style the text container
              textContainer.style.cssText = `
      position: absolute;
      bottom: -100px;
      left: 50%;
      transform: translateX(-50%);
      color: white;
      font-size: 14px;
      font-weight: bold;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
      white-space: nowrap;
      z-index: 1;
    `;

              // Add the text container to the big play button
              bigPlayButton.el().appendChild(textContainer);

              // Hide text when video starts playing
              player.on("play", () => {
                textContainer.style.display = "none";
              });

              // Show text again when video pauses or ends
              player.on("pause", () => {
                textContainer.style.display = "block";
              });

              player.on("ended", () => {
                textContainer.style.display = "block";
              });
            }
          }
        }
      );
      // Save playback time on pause
      player.on("pause", () => {
        lastPlaybackTime.current = player.currentTime();
        console.log("Paused at time:", lastPlaybackTime.current);

        if (updateVideoDuration) {
          updateVideoDuration(lastPlaybackTime.current);
        }
      });

      // Restore playback time on play
      player.on("play", () => {
        console.log("Resuming from time:", lastPlaybackTime.current);
        player.currentTime(lastPlaybackTime.current);
      });

      // Save playback time on time updates
      player.on("timeupdate", () => {
        lastPlaybackTime.current = player.currentTime();
      });

      playerRef.current = player;
    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }

    return () => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [options, onReady, updateVideoDuration, vimeoId]);

  // Skip forward by 10 seconds
  const handleSkipForward = () => {
    const player = playerRef.current;
    if (player) {
      const currentTime = player.currentTime();
      player.currentTime(currentTime + 10);
      console.log("Skipped forward to:", currentTime + 10);
    }
  };

  // Skip backward by 10 seconds
  const handleSkipBackward = () => {
    const player = playerRef.current;
    if (player) {
      const currentTime = player.currentTime();
      player.currentTime(Math.max(currentTime - 10, 0));
      console.log("Skipped backward to:", Math.max(currentTime - 10, 0));
    }
  };

  const toggleSettingsMenu = () => {
    setShowSettings(!showSettings);
  };

  const changeQuality = (quality) => {
    setCurrentQuality(quality);
    if (playerRef.current) {
      playerRef.current.src({ src: `${urlData}?quality=${quality}` });
    }
  };

  const changePlaybackSpeed = (speed) => {
    setCurrentSpeed(speed);
    if (playerRef.current) {
      playerRef.current.playbackRate(speed);
    }
    setShowSettings(false);
  };

  const toggleSubtitles = () => {
    setSubtitlesEnabled(!subtitlesEnabled);
    if (playerRef.current) {
      const tracks = playerRef.current.textTracks();
      if (tracks.length > 0) {
        tracks[0].mode = subtitlesEnabled ? "disabled" : "showing";
      }
    }
  };

  if (vimeoId) {
    return (
      <div data-vjs-player>
        <div style={{ position: "relative", paddingTop: "56.25%", width: "100%" }}>
          <VimeoPlayer
            src={playbackSrc}
            title="Vimeo video"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-vjs-player>
      <div ref={videoRef} />
      <div className={courseVideo.videFeatures}>
        <div className={courseVideo.skipTimeButton}>
          <div onClick={handleSkipBackward}>
            <SkipBackButtonIcon />
          </div>
          <div onClick={handleSkipForward}>
            <SkipForwardButtonIcon />
          </div>
        </div>
        <div className={courseVideo.skipTimeButton}>
          <div onClick={toggleSettingsMenu}>
            <SettingIcon />
          </div>
          {showSettings && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                backgroundColor: "#fff",
                padding: "30px",
                borderRadius: "5px",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                zIndex: 9999,
              }}
            >
              <h3>Settings</h3>
              {/* Quality Options */}
              <div>
                <strong>Quality</strong>
                <ul>
                  <li>
                    <div onClick={() => changeQuality("720p")}>720p</div>
                  </li>
                  <li>
                    <div onClick={() => changeQuality("1080p")}>1080p</div>
                  </li>
                  <li>
                    <div onClick={() => changeQuality("1440p")}>1440p</div>
                  </li>
                </ul>
              </div>

              {/* Playback Speed */}
              <div>
                <strong>Playback Speed</strong>
                <ul>
                  <li>
                    <div onClick={() => changePlaybackSpeed(0.5)}>0.5x</div>
                  </li>
                  <li>
                    <div onClick={() => changePlaybackSpeed(1)}>1x</div>
                  </li>
                  <li>
                    <div onClick={() => changePlaybackSpeed(1.5)}>1.5x</div>
                  </li>
                  <li>
                    <div onClick={() => changePlaybackSpeed(2)}>2x</div>
                  </li>
                </ul>
              </div>

              {/* Subtitles Toggle */}
              <div>
                <strong>Subtitles</strong>
                <div onClick={toggleSubtitles}>
                  {subtitlesEnabled ? "Disable Subtitles" : "Enable Subtitles"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoJS;
